import { QueryParam } from 'src/app/services/api.services';

export type Device = Components.Schemas.Device;
export type DeviceGroup = Components.Schemas.DeviceGroup;
export type DeviceStatus = Components.Schemas.DeviceStatus;
export type DeviceGroupStatus = Components.Schemas.DeviceGroupStatus;
export type DeviceGroupDeviceStatus =
	Components.Schemas.DeviceGroupDeviceStatus;
export type DeviceDisplayStatus = Components.Schemas.DeviceDisplayStatus;
export type DeviceAccessSource = Components.Schemas.DeviceAccessSource;
export type DeviceInstallationPhoto =
	Components.Schemas.DeviceInstallationPhoto;
export type DeviceNote = Components.Schemas.DeviceNote;
export type DeviceNotePhoto = Components.Schemas.DeviceNotePhoto;
export type DeviceNoteResponse = Components.Responses.DeviceNoteResponse;
export type DeviceSettings = Components.Schemas.DeviceSettings;
export type DeviceSettingsResponse =
	Components.Responses.DeviceSettingsResponse;
export type DeviceEditResponse = Components.Responses.DeviceEditResponse;
export type SerialNumber = Components.Schemas.SerialNumber;
export type InstallationLocationResponse =
	Components.Schemas.InstallationLocationNameFilterOptions;

export type DeviceDataRequest = (
	filter: QueryParam[]
) => Promise<{ device: Device; groupNames?: string[] }[]>;

export const emptyDevice = (): Device => ({
	id: '',
	serialNumber: '',
	decommissioned: false,
});

export const PDQ_KMS_GENERATION_1 = 'pdqkms1';
export const PDQ_SDS_GENERATION_1 = 'pdqsds1';

export const DEVICE_GENERATION_3 = 'gen3';
export const DEVICE_GENERATION_4 = 'gen4';

/** US17450 / US17444 — matches Access+ `Device.deviceMarketingName` for SentriVault. */
export const DEVICE_MARKETING_NAME_SENTRIVAULT = 'SentriVault';

export function isSentriVaultDevice(
	device: Device | undefined | null
): boolean {
	if (!device) {
		return false;
	}
	if (device.deviceMarketingName === DEVICE_MARKETING_NAME_SENTRIVAULT) {
		return true;
	}
	return (device.deviceType?.toLowerCase() ?? '').includes('sentrivault');
}

export const isPdqDevice = (device?: Device): boolean =>
	device?.deviceGeneration === PDQ_KMS_GENERATION_1 ||
	device?.deviceGeneration === PDQ_SDS_GENERATION_1;

/** True when deviceType indicates a wall-mounted unit (no hasp codes). */
export function isWallMountDevice(device: Device | undefined | null): boolean {
	return device?.deviceType?.toLowerCase()?.includes('wall mount') ?? false;
}

/** Gen3 wall mounts cannot use hasp codes. */
export function isGen3WallMountDevice(
	device: Device | undefined | null
): boolean {
	return (
		(device?.deviceGeneration?.toLowerCase() ?? '') === DEVICE_GENERATION_3 &&
		isWallMountDevice(device)
	);
}

/** True when there is at least one device and every device is PDQ (not Gen3/4/SentriVault). */
export const hasOnlyPdqDevices = (devices: { device: Device }[]): boolean =>
	devices.length > 0 && !devices.find((item) => !isPdqDevice(item.device));

/** True when every device in the set is a Gen3 wall mount. */
export const hasOnlyGen3WallMountDevices = (
	devices: { device: Device }[]
): boolean =>
	devices.length > 0 &&
	!devices.find((item) => !isGen3WallMountDevice(item.device));

export const hasSentriVaultDevices = (devices: { device: Device }[]): boolean =>
	!devices.find((item) => !isSentriVaultDevice(item.device));

/** Senturian/PDQ or SentriVault — matches backend special-grant access handling. */
export const isSpecialGrantAccessDevice = (device?: Device | null): boolean =>
	isPdqDevice(device ?? undefined) || isSentriVaultDevice(device);

/**
 * True when there is at least one device and every device is Senturian/PDQ
 * or SentriVault (including mixed groups of only those types).
 */
export const hasOnlySpecialGrantAccessDevices = (
	devices: { device: Device }[]
): boolean =>
	devices.length > 0 &&
	!devices.find((item) => !isSpecialGrantAccessDevice(item.device));
